package nl.staartvin.solarpower.data

import nl.staartvin.solarpower.data.currentuvindex.CurrentUvIndexClient
import nl.staartvin.solarpower.data.model.Location
import nl.staartvin.solarpower.data.model.UvDay
import nl.staartvin.solarpower.data.net.appHttpClient
import nl.staartvin.solarpower.data.rivm.RivmWmsClient

/**
 * Picks the best UV source(s) for a given GPS location:
 *  - Inside the NL bounding box → RIVM WMS for today (with high-res 15-min
 *    forecast + satellite-measured curves) AND currentuvindex.com for the
 *    tomorrow-onwards forecast extension that RIVM doesn't cover.
 *  - Outside NL → currentuvindex.com alone.
 *
 * Graceful degradation: if RIVM fails in NL we fall back to currentuvindex
 * entirely; if currentuvindex fails as the extension source we keep just
 * RIVM's today data (Tomorrow tab will then have no forecast).
 */
class UvRepository(
    private val global: CurrentUvIndexClient,
    private val rivm: RivmWmsClient,
) {
    /**
     * @param countryCode ISO 3166-1 alpha-2 code of the location, when known
     *   (from reverse geocoding). When `null`, falls back to a bounding-box
     *   heuristic — less precise, may misclassify points just over the German
     *   or Belgian border as NL since the bbox is a rectangle.
     */
    suspend fun fetch(location: Location, countryCode: String? = null): UvDay {
        val isNetherlands = when (countryCode) {
            "NL" -> true
            null -> location.inNlBoundingBox()   // best-effort fallback
            else -> false                         // any other country: not NL
        }
        if (!isNetherlands) {
            return global.fetch(location)
        }

        // Primary: RIVM today. If RIVM fails entirely, fall back to global.
        val rivmToday = runCatching { rivm.fetch(location) }.getOrNull()
            ?: return global.fetch(location)

        // Extension: currentuvindex for tomorrow-onwards (best-effort).
        val globalExtension = runCatching { global.fetch(location) }.getOrNull()
            ?: return rivmToday   // RIVM-only; Tomorrow tab will be empty.

        // Splice: keep all RIVM forecast points (today, 15-min granularity),
        // then append currentuvindex points that fall AFTER RIVM's last
        // sample. Effect: today's curve is the RIVM data; tomorrow's curve is
        // the (hourly, less granular) currentuvindex forecast.
        val rivmLastTime = rivmToday.forecast.maxOfOrNull { it.time }
            ?: rivmToday.now.time
        val extensionForecast = globalExtension.forecast.filter { it.time > rivmLastTime }

        return rivmToday.copy(
            forecast = rivmToday.forecast + extensionForecast,
        )
    }

    companion object {
        // Named `create` (not `default`) — `default` collides with Swift's
        // keyword usage and gets uglier names across the K/N -> Swift exporter.
        //
        // Two overloads (not default args): Kotlin/Native doesn't export default
        // arguments, so the no-arg `create()` that Swift/Android already call
        // must exist as its own function.
        fun create(): UvRepository =
            create(CurrentUvIndexClient.DEFAULT_BASE_URL, RivmWmsClient.BASE_URL)

        /**
         * Base-URL-overriding variant for the web build, which must call the
         * upstreams through a same-origin proxy to satisfy the browser's CORS
         * policy. Both are full endpoint URLs.
         */
        fun create(cuviBaseUrl: String, rivmBaseUrl: String): UvRepository {
            val http = appHttpClient()
            return UvRepository(
                global = CurrentUvIndexClient(http, cuviBaseUrl),
                rivm = RivmWmsClient(http, rivmBaseUrl),
            )
        }
    }
}

/**
 * Bounding box from HANDOFF.md (lat 50.7–53.6, lon 3.3–7.2). The RIVM WMS
 * layer's actual coverage extends slightly beyond (lat 50.25–54.0, lon 3.0–7.75)
 * but using the tighter box keeps us safely inside.
 */
internal fun Location.inNlBoundingBox(): Boolean =
    latitude in 50.7..53.6 && longitude in 3.3..7.2
